// ============================================================================
// view.webforms — /admin owns the two documents it edits
// ============================================================================
// The Webforms admin surface read TWO former-eager values, and both were
// app_store documents App's boot published into a context: ppp-webforms-v1
// through WebformsConfigContext and ppp-feed-costs-v1 through FeedCostsContext.
// webforms.admin.appStore owns both in one keyed read now, which is what lets
// `webforms` retire from the frozen app_store consumer list in
// tests/fixtures/former-eager-dependencies.json.
//
// BOTH HAD TO MOVE, OR NEITHER COULD.
//
// A view leaves a frozen consumer list only when every hydrated field it reads
// has gone — the provenance derivation is per-resource, not per-field, and both
// documents are app_store. There was also no half-way declaration available:
// `residualLegacy` has a vocabulary, and it contains no id for the feed-cost
// rates, so a surface that had localized one and not the other could not even
// be declared `partial`. It was one change or none.
//
// THE WRITES MOVED WITH THE RELEASE.
//
// `webformsAdminWriteApi` owns the authenticated app_store save posture and
// the full active-model feed-cost fan-out. The route retains local optimistic
// values for its immediate repaint, while the production maintenance cron owns
// the authenticated webform mirror publication. It therefore needs no App callback or
// legacy boot claim. The Client Errors tab also joins its existing paginated
// catalog resource, preserving its readiness/recovery behavior without an
// effect-based bypass.
//
// So `residualLegacy` is empty rather than naming a leftover: nothing on this
// page READS a boot value any more, and the direct released renderer reaches it
// without App's global dataLoaded gate.
// ============================================================================

import type {MigratedSurfaceDeclaration} from '../surfaceDataManifest';

const declaration: MigratedSurfaceDeclaration = Object.freeze({
  surface: 'view.webforms',
  obligations: Object.freeze([
    Object.freeze({id: 'webforms.admin.appStore', owner: 'permanent'}),
    Object.freeze({id: 'clientErrors.page', owner: 'permanent'}),
    Object.freeze({id: 'ownerReports.deleted', owner: 'permanent'}),
    Object.freeze({id: 'admin.backup.status', owner: 'permanent'}),
    // The Repair Bills admin panel owns its own catalog read, exactly like
    // Client Errors / Deleted / Backups above: RepairBillsPanel fetches
    // through useRouteResource('repairBills.admin') rather than a prop, so
    // this surface declares the obligation or the manifest invariant
    // (one owner per render obligation) is false. #448 mounted the panel
    // without this declaration AND without the catalog entry, which made
    // `getResource` throw during render and the tab render the error
    // boundary; the entry in routeDataCatalog completes the pair.
    Object.freeze({id: 'repairBills.admin', owner: 'permanent'}),
  ]),
  residualLegacy: Object.freeze([] as const),
});

export default declaration;
